import { useEffect, useState, useCallback } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ExternalLink, FileText, CreditCard, IdCard, Receipt, Landmark, Vote, ChevronLeft, ChevronRight, FileCheck } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import type { GovernmentLink } from "@shared/schema";
import { Carousel, CarouselContent, CarouselItem } from "@/components/ui/carousel";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

interface GovernmentLinksSectionProps {
  links?: GovernmentLink[];
  isLoading: boolean;
}

const iconMap: Record<string, any> = {
  document: FileText,
  card: CreditCard,
  id: IdCard,
  receipt: Receipt,
  bank: Landmark,
  vote: Vote,
};

export function GovernmentLinksSection({ links, isLoading }: GovernmentLinksSectionProps) {
  const [api, setApi] = useState<any>();
  const [autoplay, setAutoplay] = useState(true);
  const [selectedLink, setSelectedLink] = useState<GovernmentLink | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  useEffect(() => {
    if (!api || !autoplay) return;

    const interval = setInterval(() => {
      api.scrollNext();
    }, 4000);

    return () => clearInterval(interval);
  }, [api, autoplay]);

  const handlePrev = useCallback(() => {
    setAutoplay(false);
    api?.scrollPrev();
  }, [api]);

  const handleNext = useCallback(() => {
    setAutoplay(false);
    api?.scrollNext();
  }, [api]);

  const handleMouseEnter = () => setAutoplay(false);
  const handleMouseLeave = () => setAutoplay(true);

  if (isLoading) {
    return (
      <section className="py-12" data-testid="section-government-links">
        <div className="text-center mb-8">
          <Skeleton className="h-10 w-64 mx-auto mb-2 rounded-xl neo-card-sm" />
          <Skeleton className="h-4 w-96 mx-auto rounded-xl neo-card-sm" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="p-6 rounded-2xl bg-card neo-card">
              <div className="flex items-start gap-4 mb-4">
                <Skeleton className="w-12 h-12 rounded-xl neo-icon-box" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-6 w-32 rounded-xl neo-card-sm" />
                  <Skeleton className="h-4 w-20 rounded-xl neo-card-sm" />
                </div>
              </div>
              <Skeleton className="h-16 w-full mb-4 rounded-xl neo-card-sm" />
              <Skeleton className="h-9 w-full rounded-xl neo-button" />
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (!links || links.length === 0) {
    return null;
  }

  return (
    <section className="py-12" data-testid="section-government-links">
      <div className="text-center mb-8">
        <h2 className="font-poppins text-3xl md:text-4xl font-bold text-foreground mb-2" data-testid="heading-government-links">
          Important Government Links
        </h2>
        <p className="font-inter text-muted-foreground text-lg" data-testid="text-links-subtitle">
          Quick access to essential government services
        </p>
      </div>

      <div className="relative max-w-7xl mx-auto">
        <Carousel
          setApi={setApi}
          className="w-full"
          opts={{
            align: "start",
            loop: true,
          }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <CarouselContent className="!ml-0 gap-6 py-4">
            {links.map((link) => {
          const Icon = iconMap[link.icon] || FileText;
          const hasRequiredDocuments = (link as any).required_documents && (link as any).required_documents.trim() !== '';
          
          return (
                <CarouselItem
                  key={link.id}
                  className="!pl-0 basis-full sm:basis-1/2 lg:basis-1/3"
                >
                  <div className="h-full px-1.5">
                    <div
                      className="group p-6 h-full rounded-2xl bg-card neo-card flex flex-col transition-all duration-200 hover:shadow-lg hover:bg-primary/5 cursor-pointer"
                      data-testid={`card-link-${link.id}`}
                      onClick={() => {
                        setSelectedLink(link);
                        setIsDialogOpen(true);
                      }}
                    >
                      <div className="flex items-start gap-4 mb-4">
                        <div className="p-3 rounded-xl bg-background neo-icon-box flex-shrink-0 group-hover:bg-primary/10">
                          <Icon
                            className="w-6 h-6 text-accent group-hover:text-primary transition-colors"
                            data-testid={`icon-${link.icon}`}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3
                            className="font-poppins text-lg font-bold text-foreground mb-1"
                            data-testid={`title-link-${link.id}`}
                          >
                            {link.title}
                          </h3>
                          <Badge
                            variant="outline"
                            className="border-accent text-accent"
                            data-testid={`category-link-${link.id}`}
                          >
                            {link.category}
                          </Badge>
                        </div>
                      </div>

                      <p
                        className="font-inter text-sm text-muted-foreground mb-4 flex-grow"
                        data-testid={`description-link-${link.id}`}
                      >
                        {link.description}
                      </p>

                      <div className="mt-auto inline-flex items-center justify-center gap-2 w-full px-4 py-3 rounded-xl bg-accent text-accent-foreground font-inter text-sm font-medium neo-button group-hover:bg-primary group-hover:text-white transition-colors">
                        {hasRequiredDocuments && (
                          <FileCheck className="w-4 h-4" />
                        )}
                        View Details
                        <ExternalLink className="w-4 h-4" />
                      </div>
                    </div>
                  </div>
                </CarouselItem>
              );
            })}
          </CarouselContent>
        </Carousel>

        <div className="flex justify-center gap-4 mt-6">
          <Button
            variant="outline"
            size="icon"
            className="rounded-full h-12 w-12 shadow-md"
            onClick={handlePrev}
            data-testid="links-carousel-prev"
          >
            <ChevronLeft className="h-6 w-6" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="rounded-full h-12 w-12 shadow-md"
            onClick={handleNext}
            data-testid="links-carousel-next"
          >
            <ChevronRight className="h-6 w-6" />
          </Button>
        </div>
      </div>

      {/* Details Dialog */}
      {selectedLink && (
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="w-[92vw] max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="p-2 sm:p-2.5 rounded-lg bg-primary/10 flex-shrink-0">
                  {(() => {
                    const Icon = iconMap[selectedLink.icon] || FileText;
                    return <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />;
                  })()}
                </div>
                <div className="flex-1 min-w-0">
                  <DialogTitle className="text-lg sm:text-xl pr-2 break-words">{selectedLink.title}</DialogTitle>
                  <Badge variant="outline" className="mt-1.5 sm:mt-2 text-xs sm:text-sm">
                    {selectedLink.category}
                  </Badge>
                </div>
              </div>
              <DialogDescription className="text-sm sm:text-base text-left">
                {selectedLink.description}
              </DialogDescription>
            </DialogHeader>

            {(() => {
              const hasRequiredDocuments = (selectedLink as any).required_documents && (selectedLink as any).required_documents.trim() !== '';
              // Handle both newline-separated and comma-separated documents
              const requiredDocuments = hasRequiredDocuments 
                ? (selectedLink as any).required_documents
                    .split(/[\n,]+/)
                    .map((doc: string) => doc.trim())
                    .filter((doc: string) => doc !== '')
                : [];

              return (
                <div className="space-y-4 sm:space-y-5">
                  {hasRequiredDocuments && (
                    <div>
                      <h4 className="font-semibold text-foreground mb-2 sm:mb-3 flex items-center gap-2 text-sm sm:text-base">
                        <FileCheck className="w-4 h-4 sm:w-5 sm:h-5 text-primary flex-shrink-0" />
                        Required Documents
                      </h4>
                      <div className="p-3 sm:p-4 rounded-lg bg-muted/50 border border-border">
                        <ol className="space-y-2 sm:space-y-2.5 list-decimal list-inside">
                          {requiredDocuments.map((doc: string, index: number) => (
                            <li key={index} className="text-xs sm:text-sm text-foreground pl-1 sm:pl-2 break-words">
                              {doc}
                            </li>
                          ))}
                        </ol>
                      </div>
                    </div>
                  )}

                  <a
                    href={selectedLink.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 w-full px-4 py-2.5 sm:py-3 rounded-xl bg-primary text-primary-foreground font-inter text-sm font-medium hover:bg-primary/90 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                    onClick={() => setIsDialogOpen(false)}
                  >
                    Visit Service
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              );
            })()}
          </DialogContent>
        </Dialog>
      )}
    </section>
  );
}
